import React, { useState, useEffect } from "react";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import LanguageListing from "./LanguageListing";
import AddLanguageFormWrapper from "../Add/AddLanguageFormWrapper";
import EditLanguageFormWrapper from "../Edit/EditLanguageFormWrapper";
import { MdDeleteOutline } from "react-icons/md";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { RxPencil1 } from "react-icons/rx";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import {
  useGetLanguagesQuery,
  useDeleteLanguageMutation,
} from "src/services/LanguageServices";
import {
  setItems,
  setTotalItems,
  setIsTableLoading,
  setPage,
  setRowsPerPage,
  setSearchValue,
} from "src/redux/slices/LanguageSlice";
import { showToast } from "src/utils/showToaster";
import { UserModuleActionTypes, UserModuleNameTypes } from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const LanguageListingWrapper = (props: Props) => {
  const [selectedLanguageId, setSelectedLanguageId] = useState("");

  const [isOpenAddFrom, setIsOpenAddForm] = useState(false);
  const [isOpenEditFrom, setIsOpenEditForm] = useState(false);

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.language);

  const [deleteLangugae] = useDeleteLanguageMutation();

  const { data, isLoading, isFetching } = useGetLanguagesQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["languageName"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const dispatch = useDispatch();

  const getActionOptions = (row: any) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setIsOpenEditForm(true);
          setSelectedLanguageId(row._id);
        },
        actionName: UserModuleActionTypes.EDIT,
        moduleName: UserModuleNameTypes.LANGUAGE
      },
      {
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to Delete this Mode ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                deleteLangugae(row?._id).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", "delete successfully");
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.DELETE,
        moduleName: UserModuleNameTypes.LANGUAGE
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "languageName",
      extraClasses:'flex justify-center',
      headerName: "Name",
      flex: "flex-[1_1_0%]",
      renderCell: (row) => {
        return <span className="capitalize">{row?.languageName}</span>;
      },
    },
    {
      noAuthRequired: true,
      field: "action",
      extraClasses:'flex justify-center',
      headerName: "Action",
      flex: "flex-[1_1_0%]",
      align: "end",
      renderCell: (row: any) => {
        const options = getActionOptions(row);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  return (
    <div>
      <LanguageListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.LANGUAGE,
          actionName: UserModuleActionTypes.LIST,
          columns: columns
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />

      {isOpenAddFrom ? (
        <AddLanguageFormWrapper onClose={() => setIsOpenAddForm(false)} />
      ) : null}
      {isOpenEditFrom ? (
        <EditLanguageFormWrapper
          languageId={selectedLanguageId}
          onClose={() => setIsOpenEditForm(false)}
        />
      ) : null}
    </div>
  );
};

export default LanguageListingWrapper;
